// ─────────────────────────────────────────────────────────────────────────────
// tap_cadence.ts — Tap pattern definitions and tap qualification helpers
//
// Pattern definitions (TapPattern, RhythmGate) describe rhythmic sequences.
// The actual state-machine logic lives in TapRingLayer (tap_ring.ts) which
// manages interactive ring objects on the canvas.
//
// This file is kept as the shared type / constant home so the extension host
// (non-webview) can also introspect patterns without pulling in Canvas code.
// ─────────────────────────────────────────────────────────────────────────────

import type { StrokePoint } from '../shared/notebook_model.js';

// ── Tap qualification constants ──────────────────────────────────────────────

const TAP_MAX_DURATION_MS    = 340;
const TAP_MAX_DISPLACEMENT_PX = 36;

// ── Pattern definition ───────────────────────────────────────────────────────

/** Timing constraint on the gap between consecutive taps. */
export interface RhythmGate {
  minMs: number;              // fastest acceptable gap
  maxMs: number;              // slowest acceptable gap
}

/**
 * A TapPattern describes a sequence of taps that triggers an invocation.
 *
 * `rhythm` has length `taps − 1`: one gate per inter-tap gap.
 * If `rhythm` is shorter than `taps − 1`, the last gate is reused for
 * remaining gaps (convenient for uniform-rhythm patterns).
 */
export interface TapPattern {
  id: string;                  // unique identifier, e.g. 'thrice-knock'
  taps: number;                // required number of taps  (≥ 2)
  rhythm: RhythmGate[];        // timing between consecutive taps
  spreadPx: number;            // max distance from anchor (first tap)
  priority: number;            // higher = checked first in case of tie
  resolveColor?: string;       // colour for the resolve burst (default: golden amber)
}

// ── Recorded tap ─────────────────────────────────────────────────────────────

export interface RecordedTap {
  x: number;
  y: number;
  time: number;                // performance.now() timestamp
  strokeId: string;
}

// ── Static helpers ───────────────────────────────────────────────────────────

/** True if a completed stroke qualifies as a tap. */
export function isTap(points: StrokePoint[]): boolean {
  if (points.length < 1) return false;
  const s = points[0], e = points[points.length - 1];
  const dur = e.timestamp - s.timestamp;
  const dx = e.x - s.x, dy = e.y - s.y;
  return dur <= TAP_MAX_DURATION_MS && Math.sqrt(dx * dx + dy * dy) <= TAP_MAX_DISPLACEMENT_PX;
}

/** Extract the centroid and timestamp from a tap stroke. */
export function tapFromStroke(points: StrokePoint[], strokeId: string): RecordedTap {
  const s = points[0], e = points[points.length - 1];
  return {
    x: (s.x + e.x) / 2,
    y: (s.y + e.y) / 2,
    time: e.timestamp,
    strokeId,
  };
}

// ── Built-in patterns ────────────────────────────────────────────────────────

export const THRICE_KNOCK: TapPattern = {
  id: 'thrice-knock',
  taps: 2,
  rhythm: [{ minMs: 30, maxMs: 320 }],   // quick double-tap → wand cast
  spreadPx: 60,
  priority: 10,
  resolveColor: '#e0dcd6',                // whiteish — same as neutral/stroke
};

/**
 * Stylus Summon — tap ... tap-tap (deliberate pause then quick double).
 * The long first gap differentiates it cleanly from the 2-tap cast.
 */
export const STYLUS_SUMMON: TapPattern = {
  id: 'stylus-summon',
  taps: 3,
  rhythm: [
    { minMs: 350, maxMs: 800 },           // long first gap (deliberate pause)
    { minMs: 30,  maxMs: 300 },            // fast second gap (quick double-tap)
  ],
  spreadPx: 60,
  priority: 10,
  resolveColor: '#e8a020',                // yellow
};
